import { OrganizationRole } from '@funnellab/shared/src/types/directus-enums';
import { RecruiterItem } from './RecruiterItem';
import { Select } from '@src/components/input/Select';
import { Tooltip } from '@src/components/base/Tooltip';
import { useMemo } from 'react';
import { useRecruiterRights } from '@src/hooks/recruiter/useRecruiterRights';
import { useTranslation } from 'react-i18next';
import type {
  AcceptedRecruiter,
  Recruiter,
} from '@src/api/services/RecruiterService';

type AcceptedRecruiterItemProps = {
  recruiter: AcceptedRecruiter;
  allRecruiter: Recruiter[];
  onDelete?: () => Promise<void>;
  onRoleChange?: (role: `${OrganizationRole}`) => Promise<void>;
};

export const AcceptedRecruiterItem = ({
  recruiter,
  allRecruiter,
  onDelete,
  onRoleChange,
}: AcceptedRecruiterItemProps) => {
  const { canRemove, canUpdateRole } = useRecruiterRights({
    allRecruiters: allRecruiter,
  });

  const name = useMemo(() => {
    const name = (
      (recruiter.firstName ?? '') +
      ' ' +
      (recruiter.lastName ?? '')
    ).trim();
    return name.length > 0
      ? name
      : (recruiter.email.split('@')[0] ?? recruiter.email);
  }, [recruiter.email, recruiter.firstName, recruiter.lastName]);

  const recruiterCanBeRemoved = useMemo(
    () =>
      allRecruiter.filter((contact) => contact.accepted).length > 1 &&
      (!onRoleChange || canRemove(recruiter)),
    [allRecruiter, canRemove, onRoleChange, recruiter],
  );

  const roleCanBeChanged = useMemo(
    () => canUpdateRole(recruiter),
    [canUpdateRole, recruiter],
  );

  return (
    <RecruiterItem
      avatar={recruiter.avatar}
      displayName={name}
      email={recruiter.email}
      fallBackToInitials
      disabled={!recruiterCanBeRemoved}
      onDelete={recruiterCanBeRemoved ? onDelete : undefined}
    >
      {onRoleChange ? (
        <RoleButton
          role={recruiter.role}
          setRole={onRoleChange}
          disabled={!roleCanBeChanged}
        />
      ) : (
        <></>
      )}
    </RecruiterItem>
  );
};

const RoleButton = ({
  role,
  setRole,
  disabled,
}: {
  role: `${OrganizationRole}`;
  setRole: (role: `${OrganizationRole}`) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { t: tTooltip } = useTranslation('recruiterView');
  return (
    <Tooltip
      placement="bottom-end"
      content={tTooltip(`tooltip${role}Role`)}
      withArrow
    >
      <Select<`${OrganizationRole}`>
        options={[OrganizationRole.ADMIN, OrganizationRole.MEMBER]}
        onChange={setRole}
        displayValue={(value: `${OrganizationRole}`): string =>
          t(`role.${value}`)
        }
        value={role}
        showErrorMessage={false}
        classNameContainer="min-w-40 h-full"
        disabled={disabled}
      />
    </Tooltip>
  );
};
